<template>
  <div
    class="bg-white rounded-3xl p-4 shadow-sm flex items-center justify-between"
  >
    <div>
      <p class="text-[10px] text-neutral-light">{{ label }}</p>
      <p class="text-xl font-semibold text-primary-dark-text mt-1">
        {{ formattedValue }}
      </p>
    </div>
    <div class="w-[56px] h-[56px]">
      <ApexChart
        :options="chartOptions"
        :series="chartSeries"
        type="radialBar"
        height="56"
      />
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed } from "vue";
import ApexChart from "vue3-apexcharts";

// eslint-disable-next-line no-undef
const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: [Number, String],
    required: true,
  },
  suffix: {
    type: String,
    default: "",
  },
  percentage: {
    type: Number,
    required: true,
  },
  color: {
    type: String,
    default: "#AF56FE",
  },
});

const gradientColor = computed(() => lightenColor(props.color, 0.08));

const formattedValue = computed(() => `${props.value}${props.suffix}`);

const chartSeries = computed(() => [props.percentage]);

const chartOptions = computed(() => ({
  chart: {
    type: "radialBar",
    sparkline: {
      enabled: true,
    },
  },
  plotOptions: {
    radialBar: {
      hollow: {
        size: "10%",
      },
      track: {
        background: "#E7EBF3",
        strokeWidth: "100%",
      },
      dataLabels: {
        show: false,
      },
    },
  },
  stroke: {
    lineCap: "butt",
  },
  fill: {
    type: "gradient",
    gradient: {
      shade: "light",
      type: "diagonal1",
      shadeIntensity: 0.25,
      gradientToColors: [gradientColor.value],
      inverseColors: false,
      opacityFrom: 1,
      opacityTo: 1,
      stops: [0, 100],
    },
  },
  colors: [props.color],
  states: {
    hover: {
      filter: {
        type: "lighten",
        value: 0.05,
      },
    },
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: "none",
      },
    },
  },
}));

function lightenColor(hex, amount = 0.1) {
  const normalized = hex.replace("#", "");
  if (![3, 6].includes(normalized.length)) {
    return hex;
  }

  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;

  const num = parseInt(full, 16);
  const clamp = (value) => Math.max(0, Math.min(255, value));

  const r = clamp(((num >> 16) & 0xff) + Math.round(255 * amount));
  const g = clamp(((num >> 8) & 0xff) + Math.round(255 * amount));
  const b = clamp((num & 0xff) + Math.round(255 * amount));

  return `#${[r, g, b]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}
</script>
